import classNames from 'classnames';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    BaseOperateView,
    BaseStagePlugin,
    exportLayerOriginInfo,
    getOperateType,
    getPointRect,
    MoveListenerLevel,
    updateLayerPosition,
    useFramework,
    useIdentity,
    useMoveEventListener,
} from '@/framework';
import { isHotKeyPress, isLeftButton } from '@/utils/event-kit';
import { IExportPlugin, ILayerProps, PluginType } from '@/version-types';
import groupInnerPluginState, { getGroupPickState, useGroupPickState } from '../states/group-inner-state';

const groupInnerOperateType = 'group-inner';

// 选中图层信息
const GroupPickPlugin = React.memo(() => {
    const identity = useIdentity();
    const framework = useFramework();
    const pickIds = Array.from(useGroupPickState()((state) => state.pickIds));
    const [activeLayer, setActiveLayer] = useState<ILayerProps | null>(framework.operate.getActiveLayer());
    const activeIdRef = useRef<string | undefined>(activeLayer?.id);
    const oldInfo = useRef<ILayerProps>();
    const originLayerInfo = useRef<Record<string, ReturnType<typeof exportLayerOriginInfo>>>();
    const isMove = useRef(false);
    const [_, setRefresh] = useState(0);
    const moveOffset = useRef({ x: 0, y: 0 });

    const reset = useCallback((clear = true) => {
        const ids = getGroupPickState(identity).getPickIds();
        if (oldInfo.current) {
            if (originLayerInfo.current) {
                for (const id of ids) {
                    const res = originLayerInfo.current[id];
                    if (res) {
                        const { childOriginInfoMap, originInfo } = res;
                        updateLayerPosition(
                            identity,
                            id,
                            moveOffset.current.x,
                            moveOffset.current.y,
                            originInfo,
                            childOriginInfoMap,
                            {
                                isStart: false,
                                isEnd: true,
                                reRender: true,
                            },
                        );
                    }
                }
            }
            const layerId = oldInfo.current.id;
            const activeId = framework.operate.getActivePickLayer()?.id;
            if (activeId && activeId === layerId) {
                const layer = framework.getLayer(layerId, true);
                framework.stage.addOrUpdate(layer);
                if (oldInfo.current) {
                    framework.history.write(oldInfo.current);
                }
                console.log('layerId 1');

                framework.operate.setActiveLayer(layerId);
            }
        }
        isMove.current = false;
        oldInfo.current = undefined;
        originLayerInfo.current = undefined;
        if (ids.length) {
            setRefresh((v) => v + 1);
        }
        if (clear) {
            getGroupPickState(identity).clear();
        }
    }, []);

    // 实现组内鼠标点击
    useEffect(() => {
        const mouseDownPoint = { x: 0, y: 0 };
        const downHandle = framework.events.onMouseDown((e) => {
            if (!isLeftButton(e)) return;
            mouseDownPoint.x = e.pageX;
            mouseDownPoint.y = e.pageY;
            const hasHotKey = isHotKeyPress();
            if (!hasHotKey) {
                const operateType = getOperateType(e.target);
                if (operateType !== groupInnerOperateType) {
                    reset();
                }
            }
        }, 2);

        const upHandle = framework.events.onMouseUp((e) => {
            if (!isLeftButton(e)) return;

            const mouseUpPoint = { x: e.pageX, y: e.pageY };
            const isMove = mouseUpPoint.x !== mouseDownPoint.x || mouseUpPoint.y !== mouseDownPoint.y;
            if (isMove) return;

            const activeLayer = framework.operate.getActiveLayer();
            if (!activeLayer || !framework.checkIsFormalGroup(activeLayer) || activeLayer.lock) return;

            const mouseDownRect = getPointRect(identity, {
                x: e.pageX,
                y: e.pageY,
            });

            const groupInnerId = framework.stage.tryPickChild(mouseDownRect, activeLayer.children);

            const hasHotKey = isHotKeyPress();
            if (!hasHotKey) {
                const operateType = getOperateType(e.target);
                if (operateType === groupInnerOperateType) {
                    reset();
                    if (groupInnerId) {
                        console.log('layerId 2');
                        framework.operate.setActiveLayer(groupInnerId);
                    }
                }
                return operateType === groupInnerOperateType;
            }

            if (groupInnerId) {
                const ids: Set<string> = new Set();
                ids.add(groupInnerId);
                const activeInfo = framework.operate.getActiveInfo();
                if (activeInfo?.info.pickId) {
                    ids.add(activeInfo.info.pickId);
                    console.log('layerId 3');
                    framework.operate.setActiveLayer(activeInfo.info.id);
                }
                getGroupPickState(identity).setPickIds(Array.from(ids));
            }

            return !!groupInnerId;
        }, 2);
        return () => {
            framework.events.off(downHandle);
            framework.events.off(upHandle);
        };
    }, []);

    useEffect(() => {
        const handle = framework.events.onActiveLayerDataUpdate((layer) => {
            setActiveLayer(layer);
            activeIdRef.current = layer?.id;
            if (layer?.id !== activeIdRef.current || (layer && (!framework.checkIsFormalGroup(layer) || layer.lock))) {
                reset();
            }
        });
        return () => {
            framework.events.off(handle);
        };
    }, [reset]);

    const isNoRender =
        !pickIds.length || !activeIdRef.current || !activeLayer || !framework.checkIsFormalGroup(activeLayer);

    useEffect(() => {
        getGroupPickState(identity).setIsActive(!isNoRender);
    }, [isNoRender]);

    useMoveEventListener(
        (e) => {
            if (isNoRender) return;
            const ids = getGroupPickState(identity).getPickIds();
            const operateType = getOperateType(e.target);
            if (e.isStart && ids.length && operateType === groupInnerOperateType) {
                isMove.current = false;
                oldInfo.current = framework.getLayer(activeLayer.id, true);
                const infoMap: Record<string, ReturnType<typeof exportLayerOriginInfo>> = {};
                ids.forEach((id) => {
                    infoMap[id] = exportLayerOriginInfo(identity, id);
                });
                originLayerInfo.current = infoMap;
            }
        },
        MoveListenerLevel.PREPARE_LEVEL,
        'move-pre',
    );

    useMoveEventListener(
        (e) => {
            if (isNoRender) return;
            const operateType = getOperateType(e.target);
            if (operateType !== groupInnerOperateType) return;
            if (!oldInfo.current || !originLayerInfo.current) return;
            const ids = getGroupPickState(identity).getPickIds();
            if (!ids.length) return;
            isMove.current = true;

            const scale = framework.stage.getScale();
            const x = e.displacementX / scale;
            const y = e.displacementY / scale;

            moveOffset.current.x = x;
            moveOffset.current.y = y;

            for (const id of ids) {
                const res = originLayerInfo.current[id];
                if (res) {
                    const { childOriginInfoMap, originInfo } = res;
                    updateLayerPosition(identity, id, x, y, originInfo, childOriginInfoMap, {
                        isStart: e.isStart,
                        isEnd: e.isEnd,
                        reRender: e.isEnd,
                    });
                    if (!e.isEnd) {
                        setRefresh((v) => v + 1);
                    }
                }
            }

            if (e.isEnd) {
                reset(false);
            }
        },
        MoveListenerLevel.DEFAULT_LEVEL,
        'move-default',
    );

    if (isNoRender) return null;

    return (
        <>
            {isMove.current ? null : (
                <BaseStagePlugin originIndexOrLayerId={activeLayer.id} name="group-pick-layer">
                    <BaseOperateView layer={framework.getLayer(activeLayer.id)}>
                        <div className={classNames('group-pick-layer group-pick-layer-top')}></div>
                        <div className={classNames('group-pick-layer group-pick-layer-left')}></div>
                        <div className={classNames('group-pick-layer group-pick-layer-right')}></div>
                        <div className={classNames('group-pick-layer group-pick-layer-bottom')}></div>
                    </BaseOperateView>
                </BaseStagePlugin>
            )}
            <BaseStagePlugin originIndexOrLayerId={pickIds[0]} name="group-pick-layer">
                {pickIds.map((id) => {
                    const layer = framework.getLayer(id);
                    return (
                        <BaseOperateView key={id} layer={layer}>
                            <div
                                data-operate-type={groupInnerOperateType}
                                className={classNames('group-pick-item', {
                                    'is-move': isMove.current,
                                })}
                            ></div>
                        </BaseOperateView>
                    );
                })}
            </BaseStagePlugin>
        </>
    );
});

const exportPlugin: IExportPlugin = {
    type: PluginType.STAGE,
    sort: 1,
    name: 'group-pick-layer',
    render() {
        return <GroupPickPlugin />;
    },
    createState() {
        return [groupInnerPluginState];
    },
};

export default exportPlugin;
